import React, { useState } from "react";
import clsx from "clsx";

import { useAppSelector } from "@/store/hooks";

import { appDate } from "@/utils/date";

import AppBox from "@/components/AppBox";
import AppInput from "@/components/AppInput";
import AppText from "@/components/AppText";
import AppIcon from "@/components/AppIcon";
import AppTooltip from "@/components/AppTooltip";

interface Note {
  created_at: string;
  note: string;
  id: number;
}

interface NoteItemProps {
  note: Note;
  onSave: (id: number, updatedNote: string) => void;
  onDelete: (id: number) => void;
}

const NoteItem: React.FC<NoteItemProps> = ({ note, onSave, onDelete }) => {
  const { isMobile } = useAppSelector((state) => state.app);

  const [isEdit, setIsEdit] = useState(false);
  const [tempNote, setTempNote] = useState(note.note);

  const handleEdit = () => setIsEdit(true);
  const handleCancel = () => {
    setIsEdit(false);
    setTempNote(note.note); // Reset to original note
  };
  const handleSave = () => {
    onSave(note.id, tempNote); // Call save handler with updated note
    setIsEdit(false); // Exit edit mode
  };

  return (
    <AppBox
      column
      gap={2}
      className="group border-b px-1 pb-4 mb-4 last:mb-0 last:border-0"
    >
      {/* Note Header */}
      <AppBox justify="between" align="center">
        <AppText variant="h6" color="disabled">
          { appDate(note.created_at) }
        </AppText>
        <AppBox gap={2}>
          {isEdit ? (
            <>
              <AppTooltip title="Cancel Editing">
                <AppIcon
                  name="x"
                  className="text-error hover:text-error-hover cursor-pointer"
                  onClick={handleCancel}
                />
              </AppTooltip>
              <AppTooltip title="Save Note">
                <AppIcon
                  name="check"
                  className="text-primary hover:text-primary-hover cursor-pointer"
                  onClick={handleSave}
                />
              </AppTooltip>
            </>
          ) : (
            <AppBox className={clsx(
              "opacity-0 cursor-pointer gap-2",
              {
                "group-hover:opacity-100": !isEdit,
                "opacity-100": isMobile
              }
            )}>
              <AppTooltip title="Edit Note">
                <AppIcon
                  name="edit"
                  className="cursor-pointer"
                  onClick={handleEdit}
                />
              </AppTooltip>
              <AppTooltip title="Delete Note">
                <AppIcon
                  name="trash"
                  className="text-error hover:text-error-hover cursor-pointer"
                  onClick={() => onDelete(note.id)}
                />
              </AppTooltip>
            </AppBox>
          )}
        </AppBox>
      </AppBox>

      {/* Note Content */}
      <AppInput
        label=""
        multiline
        rows={2}
        value={isEdit ? tempNote : note.note}
        onChange={(value) => isEdit && setTempNote(value)}
        fullWidth
        inputClassName="text-gray-900"
        disabled={!isEdit}
        className={!isEdit ? "border-transparent" : ""}
      />
    </AppBox>
  );
};

export default NoteItem;